import React from "react";
import { motion } from "framer-motion";
import { formatMoney } from "../../utils/formatMoney";
import { Building2, Hash, DollarSign, X } from "lucide-react";

/**
 * Components
 */
import FormOffer from "blocks/form-offer/form-offer";
import useModal from "hooks/use-modal";

const ModalPaymentDetails = (props) => {
  const { account, offers, mode, greaterThan, prefillAmount } = props.modalProps;
  const { closeModal } = useModal();

  // Animation variants
  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 },
    },
  };

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      {/* Modal Header */}
      <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 sm:gap-4 pr-10"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
              {account.chargeoff_creditor?.name || account.creditor.name}
            </h2>
            <p className="text-blue-100 text-sm sm:text-base">
              Choose your payment option
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Body */}
      <motion.div
        className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0 overscroll-contain"
        variants={contentAnimation}
        initial="hidden"
        animate="visible"
      >
        {/* Account Details Card */}
        <motion.div
          variants={itemAnimation}
          className="bg-gray-800/50 backdrop-blur rounded-xl p-4 mb-6 border border-gray-700/50"
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <Hash className="w-4 h-4" />
                <span className="text-sm">Account</span>
              </div>
              <span className="text-white font-medium">
                #{account.chargeoff_creditor?.ref_id || account.creditor.ref_id}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm">Current Balance</span>
              </div>
              <span className="text-2xl font-bold text-white">
                ${formatMoney(account.balance_current)}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Form Section */}
        <motion.div variants={itemAnimation}>
          <FormOffer
            account={account}
            offers={offers}
            mode={mode}
            greaterThan={greaterThan}
            prefillAmount={prefillAmount}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ModalPaymentDetails;
